import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { 
  Mail, Send, Users, Search, Check, X, 
  ChevronLeft, ChevronRight, History
} from 'lucide-react';
import { adminFetch } from './AdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const AdminEmail = () => {
  const { adminToken } = useOutletContext();
  const [tab, setTab] = useState('compose'); // compose, users, logs
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  
  // Email form
  const [subject, setSubject] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [sending, setSending] = useState(false);

  // Fetch users for selection
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 100 });
      if (userSearch) params.append('search', userSearch);
      
      const data = await adminFetch(`/admin/users?${params}`, {}, adminToken);
      setUsers(data.users);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch email logs
  const fetchLogs = async () => {
    setLoading(true);
    try {
      const data = await adminFetch('/admin/email/logs?limit=50', {}, adminToken);
      setLogs(data.logs || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 'users') {
      fetchUsers();
    } else if (tab === 'logs') {
      fetchLogs();
    }
  }, [tab, adminToken]);

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAllUsers = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(u => u.user_id));
    }
  };

  const handleSendEmail = async () => {
    if (!subject.trim() || !htmlContent.trim()) {
      toast.error('Please fill in subject and content');
      return;
    }

    if (selectedUsers.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }

    setSending(true);
    try {
      const result = await adminFetch('/admin/email/to-users', {
        method: 'POST',
        body: JSON.stringify({
          user_ids: selectedUsers,
          subject,
          html_content: htmlContent
        })
      }, adminToken);

      if (result.mocked) {
        toast.success(`Email sent (mocked) to ${selectedUsers.length} users`);
      } else {
        toast.success(`Email sent to ${result.results?.sent || selectedUsers.length} users`);
      }
      
      // Reset form
      setSubject('');
      setHtmlContent('');
      setSelectedUsers([]);
      setTab('logs');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Email Center</h1>
        <p className="text-gray-400 mt-1">Send emails to users</p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-gray-700 pb-2">
        {[
          { id: 'compose', label: 'Compose', icon: Mail },
          { id: 'users', label: 'Select Users', icon: Users },
          { id: 'logs', label: 'History', icon: History },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              tab === id 
                ? 'bg-emerald-600 text-white' 
                : 'text-gray-400 hover:bg-gray-700'
            }`}
          >
            <Icon size={18} />
            {label}
            {id === 'users' && selectedUsers.length > 0 && (
              <span className="ml-1 px-2 py-0.5 bg-emerald-500 rounded-full text-xs">
                {selectedUsers.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Compose Tab */}
      {tab === 'compose' && (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-2">Recipients</label>
            <div className="flex items-center gap-2">
              <div className="flex-1 px-4 py-2 bg-gray-700 rounded-lg text-gray-300">
                {selectedUsers.length === 0 
                  ? 'No users selected' 
                  : `${selectedUsers.length} user(s) selected`}
              </div>
              <Button
                variant="outline"
                onClick={() => setTab('users')}
                className="border-gray-600"
              >
                <Users size={16} className="mr-2" />
                Select Users
              </Button>
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">Subject</label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject..."
              className="bg-gray-700 border-gray-600 text-white"
              data-testid="email-subject-input"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">Content (HTML)</label>
            <textarea
              value={htmlContent}
              onChange={(e) => setHtmlContent(e.target.value)}
              placeholder="<h1>Hello!</h1><p>Your email content here...</p>"
              className="w-full h-64 px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid="email-content-input"
            />
            <p className="text-xs text-gray-500 mt-1">Use inline CSS for styling. External stylesheets won't work in emails.</p>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleSendEmail}
              disabled={sending || selectedUsers.length === 0}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="send-email-btn"
            >
              <Send size={16} className="mr-2" />
              {sending ? 'Sending...' : 'Send Email'}
            </Button>
          </div>
        </div>
      )}

      {/* Users Tab */}
      {tab === 'users' && (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <Input
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchUsers()}
                placeholder="Search users..."
                className="pl-10 bg-gray-700 border-gray-600 text-white"
              />
            </div>
            <Button onClick={fetchUsers} variant="outline" className="border-gray-600">
              Search
            </Button>
            <Button onClick={selectAllUsers} variant="outline" className="border-gray-600">
              {selectedUsers.length === users.length ? 'Deselect All' : 'Select All'}
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto divide-y divide-gray-700">
              {users.map((user) => (
                <div 
                  key={user.user_id}
                  onClick={() => toggleUserSelection(user.user_id)}
                  className={`flex items-center gap-3 p-3 cursor-pointer transition-colors ${
                    selectedUsers.includes(user.user_id) 
                      ? 'bg-emerald-600/20' 
                      : 'hover:bg-gray-700'
                  }`}
                >
                  <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                    selectedUsers.includes(user.user_id)
                      ? 'bg-emerald-600 border-emerald-600'
                      : 'border-gray-500'
                  }`}>
                    {selectedUsers.includes(user.user_id) && <Check size={14} className="text-white" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-white">{user.name || 'Unknown'}</p>
                    <p className="text-gray-400 text-sm">{user.email}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end">
            <Button onClick={() => setTab('compose')} className="bg-emerald-600 hover:bg-emerald-700">
              Continue to Compose ({selectedUsers.length})
            </Button>
          </div>
        </div>
      )}

      {/* Logs Tab */}
      {tab === 'logs' && (
        <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : logs.length === 0 ? (
            <div className="p-12 text-center">
              <Mail className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No email logs found</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-700">
              {logs.map((log, i) => (
                <div key={i} className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium">{log.subject}</p>
                      <p className="text-gray-400 text-sm">
                        {log.type === 'bulk' 
                          ? `Sent to ${log.recipient_count} recipients`
                          : `Sent to ${log.recipient}`}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-gray-400 text-sm">{new Date(log.created_at).toLocaleString()}</p>
                      <p className="text-gray-500 text-xs">by {log.sent_by}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminEmail;
